import React, { useState, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../utils/apiBase';

const LocationCapture = () => {
    const [location, setLocation] = useState({ latitude: "", longitude: "", address: "", timestamp: "" });
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [cameraStarted, setCameraStarted] = useState(false); // ✅ Track camera state
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // ✅ Fetch Location Automatically
    const fetchLocation = async () => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const lat = position.coords.latitude;
                        const long = position.coords.longitude;
                        const timestamp = new Date().toLocaleString(); // ✅ Get current date & time

                        // ✅ Use OpenStreetMap Nominatim API (Free)
                        const geocodeURL = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${long}`;
                        try {
                            const response = await axios.get(geocodeURL);
                            const address = response.data.display_name || "Unknown location";

                            const locationData = { latitude: lat, longitude: long, address, timestamp };
                            setLocation(locationData);
                            resolve(locationData);
                        } catch (error) {
                            console.error("Error fetching location:", error);
                            reject("Location not found");
                        }
                    },
                    (error) => {
                        console.error("Error getting location:", error);
                        alert("Location access denied!");
                        reject(error);
                    }
                );
            } else {
                alert("Geolocation is not supported by this browser.");
                reject("Geolocation not supported");
            }
        });
    };

    // ✅ Start Camera (Only shows "Capture" button after starting)
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            videoRef.current.srcObject = stream;
            setCameraStarted(true); // ✅ Show "Capture Photo" button
        } catch (error) {
            console.error("Error accessing webcam:", error);
            alert("Webcam access denied or not available.");
        }
    };

    // ✅ Take a photo using the webcam
    const handleCapturePhoto = async () => {
        await fetchLocation(); // ✅ Fetch location before capturing

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        // ✅ Capture image from video
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = canvas.toDataURL("image/png"); // Convert to Base64

        setPhoto(imageData);
        setPreview(imageData);

        // ✅ Stop the webcam after capturing
        const stream = video.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());

        setCameraStarted(false); // ✅ Hide "Capture Photo" button
    };

    // ✅ Select Image File from Device
    const handleChooseFile = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = async () => {
                await fetchLocation(); // ✅ Fetch location automatically
                setPhoto(reader.result);
                setPreview(reader.result);
            };
        }
    };

    // ✅ Upload Data to Backend
    const handleSubmit = async () => {
        if (!photo || !location.latitude || !location.longitude || !location.address || !location.timestamp) {
            alert("Please capture/select a photo and enable location first!");
            return;
        }

        const data = {
            latitude: location.latitude,
            longitude: location.longitude,
            address: location.address,
            timestamp: location.timestamp, // ✅ Send date & time
            photo: photo, // ✅ Send Base64 image
        };

        try {
            setUploading(true);
            const response = await axios.post(`${API_BASE_URL}/api/agentLocation/location`, data);
            alert("Data uploaded successfully!");
            console.log(response.data);
        } catch (error) {
            console.error("Upload error:", error);
            alert("Upload failed!");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ padding: "20px" }}>
            <h2>Automated Location & Photo Capture</h2>
            
            {/* ✅ Automatically Show Location */}
            {location.latitude && (
                <>
                    <p><strong>Latitude:</strong> {location.latitude}, <strong>Longitude:</strong> {location.longitude}</p>
                    <p><strong>Address:</strong> {location.address}</p>
                    <p><strong>Date & Time:</strong> {location.timestamp}</p>
                </>
            )}

            {/* ✅ Start Camera */}
            <button onClick={startCamera}>📷 Start Camera</button>
            <video ref={videoRef} autoPlay playsInline width="200" height="150"></video>

            {/* ✅ Show "Capture Photo" Button Only After Starting Camera */}
            {cameraStarted && (
                <button onClick={handleCapturePhoto}>📸 Capture Photo</button>
            )}

            <canvas ref={canvasRef} width="200" height="150" style={{ display: "none" }}></canvas>

            {/* ✅ Choose Image File */}
            <input type="file" accept="image/*" onChange={handleChooseFile} />
            <label>📂 Choose Image</label>

            {/* ✅ Show Image Preview */}
            {preview && <img src={preview} alt="Captured" width="200" height="150" />}

            {/* ✅ Upload Data to Backend */}
            <button onClick={handleSubmit} disabled={uploading}>
                {uploading ? "Uploading..." : "🚀 Upload Data"}
            </button>
        </div>
    );
};

export default LocationCapture;
